import { ShieldCheck, Timer, BadgeCheck, Stethoscope, Wrench, ArrowUpFromLine } from "lucide-react";
import { Reveal, SectionHeading, waLink } from "./shared";

const BADGES = [
  { icon: ShieldCheck, title: "Quality Screens", desc: "Grade-A panels, true colours" },
  { icon: Timer, title: "Fast Service", desc: "Most screens done same day" },
  { icon: BadgeCheck, title: "Reliable Results", desc: "Tested before handover" },
];

const MANTRA = [
  { icon: Stethoscope, title: "Diagnose", desc: "We find the problem" },
  { icon: Wrench, title: "Repair", desc: "We repair it right" },
  { icon: ArrowUpFromLine, title: "Upgrade", desc: "Better performance" },
];

export const ScreenRepair = () => (
  <section id="screen-repair" data-testid="screen-repair-section" className="relative py-24 sm:py-32 bg-navy/40 overflow-hidden">
    <div className="absolute top-0 right-1/4 w-[26rem] h-[26rem] rounded-full bg-brand-cyan/8 blur-[140px] pointer-events-none" />
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <SectionHeading
        eyebrow="Before: Cracked — After: Crystal Clear"
        title="Screen"
        accent="replacement."
        sub="Shattered panel, flickering lines or dead pixels — AFZ fits quality replacement screens for laptops and MacBooks, tested before handover."
        align="center"
      />

      <Reveal delay={0.15}>
        <div className="mt-14 relative max-w-4xl mx-auto clip-corner border border-brand-lime/30 overflow-hidden shadow-[0_0_50px_rgba(170,255,0,0.12)]">
          <img
            src="/assets/collateral-screen.jpg"
            alt="Before cracked screen, after crystal clear — AFZ screen replacement"
            className="w-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-void/60 via-transparent to-transparent" />
        </div>
      </Reveal>

      <div className="mt-10 grid sm:grid-cols-3 gap-5 max-w-4xl mx-auto">
        {BADGES.map((b, i) => (
          <Reveal key={b.title} delay={i * 0.08}>
            <div className="flex items-center gap-4 bg-card/80 border border-slate-800 rounded-xl p-5 hover:border-brand-lime/50 transition-all duration-300">
              <div className="w-11 h-11 shrink-0 rounded-full border-2 border-brand-lime/60 bg-brand-lime/10 flex items-center justify-center text-brand-lime shadow-[0_0_16px_rgba(170,255,0,0.25)]">
                <b.icon className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-display text-lg font-bold uppercase text-white leading-none">{b.title}</h3>
                <p className="text-xs text-slate-400 mt-1">{b.desc}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.2}>
        <div className="mt-8 max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6 border border-white/10 rounded-2xl p-6 sm:p-8 bg-card/60">
          <div className="flex flex-wrap justify-center gap-x-10 gap-y-4">
            {MANTRA.map((m) => (
              <div key={m.title} className="flex items-center gap-3">
                <m.icon className="w-6 h-6 text-brand-cyan" />
                <div>
                  <p className="font-display font-bold uppercase text-white leading-none">{m.title}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500 mt-1">{m.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <a
            data-testid="screen-quote-button"
            href={waLink("Hi AFZ Tech Solution, I need a screen replacement for my laptop. Can I get a quote?")}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 inline-flex items-center gap-2 bg-brand-lime text-void font-display font-bold uppercase px-7 py-3.5 rounded-full hover:bg-brand-limedark hover:scale-[1.03] transition-all duration-300 shadow-[0_0_24px_rgba(170,255,0,0.35)]"
          >
            Get Screen Quote
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);
